import type { Breakpoints } from 'types';
import { buildPictureAttributes } from 'components/Picture/utils';

const postHero: Breakpoints = {
  base: 343,
  xs: 524,
  sm: 676,
  md: 592,
  lg: 592,
};

const postContent: Breakpoints = {
  base: 284,
  xs: 434,
  sm: 558,
  md: 488,
  lg: 488,
};

const indexHero: Breakpoints = {
  base: 343,
  xs: 524,
  sm: 676,
  md: 592,
  lg: 592,
};

const indexCard: Breakpoints = {
  base: 280,
  xs: 280,
  sm: 280,
  md: 280,
  lg: 280,
};

const moreCard: Breakpoints = {
  base: 343,
  xs: 524,
  sm: 322,
  md: 436,
  lg: 280,
};

const slots = {
  indexCard,
  indexHero,
  moreCard,
  postContent,
  postHero,
};

type BlogPictureProps = {
  alt: string;
  className?: string;
  crop?: boolean;
  focalPointX?: number;
  focalPointY?: number;
  nativeWidth: number;
  ratio?: string;
  slot: keyof typeof slots;
  url: string;
};

const BlogPicture = ({
  className = '',
  url,
  alt,
  ratio,
  focalPointX = 0.3,
  focalPointY = 0.5,
  slot,
  crop = false,
  nativeWidth,
}: BlogPictureProps) => {
  const {
    aspectRatios,
    chunkedClasses,
    className: classWithBase,
    hasValidAr,
    screens,
  } = buildPictureAttributes(className, ratio ? '' : 'aspect-ratio-7by5');

  const imgWidths = slots[slot];

  interface SanityUrl {
    aspectRatio: (typeof aspectRatios)[keyof Breakpoints];
    dpr: 1 | 2;
    focalPointX: number;
    focalPointY: number;
    url: string;
    width: number;
  }

  const showWarnings = !hasValidAr && !ratio && process.env.ENVIRONMENT !== 'production';

  if (showWarnings)
    console.error(
      `Image uses an unsupported aspect ratio. Either edit the image to match one of the ratios already defined in the Tailwind theme file or define a new one.`,
    );

  function buildSanityUrl({ width, aspectRatio, dpr, url, focalPointX, focalPointY }: SanityUrl) {
    let height;

    if (aspectRatio) {
      const [aspectWidth, aspectHeight] = (aspectRatio as string).split(':');
      const ratio = width / parseInt(aspectWidth, 10);
      height = Math.ceil(ratio * parseInt(aspectHeight, 10));
    }

    return `${url}?fm=jpg&w=${width}&auto=format&dpr=${dpr}&${
      crop && height ? `fit=crop&crop=focalpoint&fp-x=${focalPointX}&fp-y=${focalPointY}&h=${height}` : 'fit=clip'
    }`;
  }

  return (
    <div className={`flex w-full justify-center ${chunkedClasses.noMatch?.join(' ')}`}>
      <div className={`max-w-full ${classWithBase.join(' ')}`} style={{ aspectRatio: ratio, width: nativeWidth ? nativeWidth : 'auto' }}>
        <picture className={`inline h-auto w-auto`}>
          <>
            {Object.keys(aspectRatios)
              .reverse()
              .map((elm) => (
                <source
                  key={elm}
                  media={elm === 'base' ? undefined : `(min-width: ${screens[elm as keyof Breakpoints]})`}
                  srcSet={`${buildSanityUrl({
                    url,
                    focalPointX,
                    focalPointY,
                    aspectRatio: aspectRatios[elm as keyof Breakpoints],
                    dpr: 1,
                    width:
                      (imgWidths[elm as keyof Breakpoints] as number) > nativeWidth
                        ? nativeWidth
                        : (imgWidths[elm as keyof Breakpoints] as number),
                  })} 1x,
                  ${buildSanityUrl({
                    url,
                    focalPointX,
                    focalPointY,
                    aspectRatio: aspectRatios[elm as keyof Breakpoints],
                    dpr: 2,
                    width:
                      (imgWidths[elm as keyof Breakpoints] as number) > nativeWidth
                        ? nativeWidth
                        : (imgWidths[elm as keyof Breakpoints] as number),
                  })} 2x`}
                />
              ))}

            <img className={`block h-full w-full object-cover`} loading="lazy" alt={alt} />
          </>
        </picture>
      </div>
    </div>
  );
};

export default BlogPicture;
